import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useParameterizedQuery } from 'react-fetching-library';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Tooltip, IconButton, Card, CardHeader, Stack, CircularProgress, Box } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { attachmentsColumns } from '../../../../../services/column-definitions';
import { addAttachmentAction, deleteAttachmentAction, downloadAttachmentAction } from '../../../../../services/request';
import { useApplication } from '../../ApplicationsContext';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import toast from 'react-hot-toast';
import { PreviewFileModal } from './PreviewFileModal';


export default function Attachments () {
  const { rowData, setRowData } = useApplication();
  const { query } = useParameterizedQuery(downloadAttachmentAction);
  const { mutate, loading } = useMutation(addAttachmentAction);
  const { mutate: deleteMutate } = useMutation(deleteAttachmentAction);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [deleteData, setDeleteData] = React.useState({});
  const [activeDocument, setActiveDocument] = React.useState(null);
  const [renameModalOpen, setRenameModalOpen] = React.useState(false);
  const [fileToRename, setFileToRename] = React.useState(null);
  const [newFileName, setNewFileName] = React.useState('');
  const [filesToUpload, setFilesToUpload] = React.useState([]);
  const { isDragActive, getRootProps, getInputProps, open } = useDropzone(
    {
      noClick: true,
      multiple: true,
      onDropAccepted(files) {
        handleUploadFiles(files);
      },
      onDropRejected() {
        toast.error('Wystpąpił błąd');
      }
    }
  );

  const handleOpenDeleteModal = rowData => {
    setDeleteData(rowData);
    setConfirmDeleteOpen(true);
  };

  const handleSetActiveDocument = async (id, name, file_url) => {
    const { payload, error, loading } = await query(id);

    if (!error) {
      const url = window.URL.createObjectURL(payload);
      setActiveDocument({ uri: url, fileName: name, file_url });
    } else {
      toast.error('Nie udało się otworzyć załącznika');
    }
  };

  const handleDelete = async id => {
    const { error } = await deleteMutate(id);
    if (!error) {
      setRowData(prev => ({ ...prev, attachments: prev.attachments.filter(element => element.id !== id) }));
      toast.success('Załącznik został usunięty');
    }

    if (error) {
      toast.error('Nie udało się usunąć załącznika');
    }
  };

  const handleUploadFiles = files => {
    // Otwórz modal dla pierwszego pliku
    setFilesToUpload(files);
    setFileToRename(files[0]);
    setNewFileName(files[0]?.name || '');
    setRenameModalOpen(true);
  };

  const handleConfirmRename = async () => {
    const formData = new FormData();
    formData.append('file', fileToRename);
    formData.append('name', newFileName); // Dodanie nowej nazwy pliku

    const { error, payload } = await mutate(rowData.id, formData);

    if (!error) {
      setRowData(prev => ({ ...prev, attachments: [...payload, ...prev.attachments] }));
      toast.success('Załącznik został dodany');
    } else {
      toast.error('Nie udało się dodać załącznika');
    }

    // Obsługa kolejnych plików
    const remainingFiles = filesToUpload.slice(1);
    if (remainingFiles.length > 0) {
      setFilesToUpload(remainingFiles);
      setFileToRename(remainingFiles[0]);
      setNewFileName(remainingFiles[0]?.name || '');
    } else {
      setRenameModalOpen(false);
      setFilesToUpload([]);
    }
  };

  return (
    <>
      <Card style={isDragActive ? { height: '100%', opacity: 0.65, border: '2px solid #ddd', borderStyle: 'dashed' } : { height: '100%', border: '2px solid transparent' }} {...getRootProps()}>
        <input {...getInputProps()}/>
        <CardHeader action={
          loading
            ? <Box sx={{ p: '8px', height: '40px' }}><CircularProgress size={24}/></Box>
            : <Tooltip title="Dodaj plik" >
              <IconButton
                color="primary"
                component="label"
                onClick={open}
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
        }/>
        <DataGridPro
          rows={rowData.attachments === undefined ? [] : rowData.attachments}
          columns={attachmentsColumns(handleOpenDeleteModal, handleSetActiveDocument)}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Nie dodano żadnych plików
              </Stack>
            )
          }}
        />
        {activeDocument !== null ? <PreviewFileModal activeDocument={activeDocument} handleClose={() => setActiveDocument(null)}/> : null}
        <ConfirmDialog
          open={confirmDeleteOpen}
          setOpen={setConfirmDeleteOpen}
          onConfirm={() => handleDelete(deleteData.id)}
        >
          <p>Czy na pewno chcesz usunąć <b>{deleteData.file_name}</b>?</p>
        </ConfirmDialog>
      </Card>
      <Dialog open={renameModalOpen} onClose={() => setRenameModalOpen(false)}>
        <DialogTitle>Zmień nazwę</DialogTitle>
        <DialogContent sx={{ minWidth: '400px' }}>
          <Box sx={{ height: 20 }}></Box>
          <TextField
            fullWidth
            label="Nazwa pliku"
            value={newFileName}
            onChange={e => setNewFileName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={() => setRenameModalOpen(false)}>Anuluj</Button> */}
          <Button onClick={handleConfirmRename} color="primary" variant="contained">
            Dodaj plik
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
